import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import ForumIcon from '@mui/icons-material/Forum';
import { usePermissions } from '../../hooks/usePermissions';
import { diskussionService } from '../../services/diskussionService';
import * as P from '../../constants/permissions';

export interface DiskussionAnchorQuery {
  refType: string;
  /** Fehlt bei refType hof_uebersicht */
  refId?: number;
  hof_jahr?: number;
  hof_monat?: number;
  hof_mitarbeiter_id?: number;
}

/** Slug der passenden Fach-Kategorie (Migration s3t4u5v6w7x8); sonst „allgemein“. */
const REF_TYPE_TO_CATEGORY_SLUG: Record<string, string> = {
  projekt: 'projekte',
  audit: 'audits',
  kunde: 'kunden',
  schwachstelle: 'schwachstellen',
  durchfuehrungszeitraum: 'durchfuehrung',
  tool: 'tools',
  mitarbeiter: 'mitarbeiter',
  hof_uebersicht: 'hall-of-fame',
  hof_monat_mitarbeiter: 'hall-of-fame',
};

/** Schnellzugriff: Anker-Filter + passende Kategorie (z. B. Kunden → Kategorie „Kunden“). */
const DiskussionContextLink: React.FC<{
  anchor: DiskussionAnchorQuery;
  label?: string;
  /** Wird als vorgeschlagener Thread-Titel übergeben (z. B. Projekt- oder Auditname). */
  objectLabel?: string;
}> = ({ anchor, label = 'Diskussion zu diesem Eintrag', objectLabel }) => {
  const { hasPermission } = usePermissions();
  const [katId, setKatId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!hasPermission(P.DISKUSSION_VIEW)) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows = await diskussionService.listKategorien();
        const slug = REF_TYPE_TO_CATEGORY_SLUG[anchor.refType] ?? 'allgemein';
        const preferred =
          rows.find((k) => k.slug === slug) ||
          rows.find((k) => k.slug === 'allgemein') ||
          rows[0];
        if (!cancelled) {
          setKatId(preferred?.id ?? null);
        }
      } catch {
        if (!cancelled) setError('Kategorien konnten nicht geladen werden');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasPermission, anchor.refType]);

  const to = useMemo(() => {
    if (katId == null) return null;
    const q = new URLSearchParams();
    q.set('refType', anchor.refType);
    if (anchor.refType !== 'hof_uebersicht' && anchor.refId != null) q.set('refId', String(anchor.refId));
    if (anchor.hof_jahr != null) q.set('hof_jahr', String(anchor.hof_jahr));
    if (anchor.hof_monat != null) q.set('hof_monat', String(anchor.hof_monat));
    if (anchor.hof_mitarbeiter_id != null) q.set('hof_mitarbeiter_id', String(anchor.hof_mitarbeiter_id));
    const ol = objectLabel?.trim();
    if (ol) q.set('contextLabel', ol);
    return `/diskussion/kategorie/${katId}?${q.toString()}`;
  }, [katId, anchor, objectLabel]);

  if (!hasPermission(P.DISKUSSION_VIEW)) {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={18} />
      </Box>
    );
  }

  if (error || !to) {
    return <Alert severity="warning">{error || 'Keine Diskussions-Kategorie verfügbar'}</Alert>;
  }

  return (
    <Button
      component={RouterLink}
      to={to}
      variant="outlined"
      size="small"
      startIcon={<ForumIcon />}
      sx={{ alignSelf: 'flex-start', mt: 1 }}
    >
      {label}
    </Button>
  );
};

export default DiskussionContextLink;
